import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import MoonOutlined from '@ant-design/icons/MoonOutlined';
import SunOutlined from '@ant-design/icons/SunOutlined';
import UserOutlined from '@ant-design/icons/UserOutlined';
import { useQueryClient } from '@tanstack/react-query';
import App from 'antd/es/app';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import Layout from 'antd/es/layout';
import { type MenuProps } from 'antd/es/menu';
import Modal from 'antd/es/modal';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Header from './Header';
import HelpButton from './HelpButton';
import MenuButton from './MenuButton';
import { useTheme } from './ThemeProvider';

import { apiClient } from '@queries/axios';
import { useProjectId, useTeamId } from '@utils/router';

const StyledMenuItems = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Container = styled(Layout.Header)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-left: 12px;
  padding-right: 12px;
  background-color: ${({ theme }) => theme.token.colorBgContainer};
`;

const StyledUserIcon = styled(UserOutlined)`
  font-size: 20px;
  cursor: pointer;
`;

export const TopBar = () => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const projectId = useProjectId();
  const teamId = useTeamId();

  const onLogout = async () => {
    setIsModalOpen(false);

    // Destructure message.loading once to prevent multiple state updates
    const loadingMessage = message.loading('Logging out...');
    try {
      // logout which will clear the cookie and session on the backend
      await apiClient.post('/authentication/logout');
      loadingMessage();

      // clear all query client state
      void queryClient.clear();
    } catch (error) {
      loadingMessage();
      void message.error('Failed to logout');
    } finally {
      navigate('/auth/login');
    }
  };

  // Memoize menu items to prevent recreation on every render
  const menuItems = useMemo<MenuProps['items']>(
    () => [
      {
        key: 'theme',
        label: (
          <div
            onClick={toggleTheme}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
            }}
          >
            {theme === 'light' ? <MoonOutlined /> : <SunOutlined />}
            <span>Toggle Theme</span>
          </div>
        ),
      },
      {
        key: 'logout',
        label: (
          <div
            onClick={() => setIsModalOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'pointer',
            }}
          >
            <LogoutOutlined />
            <span>Logout</span>
          </div>
        ),
      },
    ],
    [theme, toggleTheme],
  );

  const linkUrl = useMemo(() => {
    if (projectId && teamId) {
      return `/team/${teamId}/project/${projectId}`;
    }

    if (teamId) {
      return `/team/${teamId}`;
    }

    return '/';
  }, [projectId, teamId]);

  return (
    <>
      <Container>
        <Link
          to={linkUrl}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Header />
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <HelpButton />
          <Dropdown
            trigger={['click']}
            menu={{ items: menuItems }}
            placement="bottomRight"
          >
            <Button shape="circle" icon={<StyledUserIcon />} />
          </Dropdown>
          <StyledMenuItems>
            <MenuButton />
          </StyledMenuItems>
        </div>
      </Container>
      <Modal
        title="Confirm Logout"
        open={isModalOpen}
        onOk={onLogout}
        onCancel={() => setIsModalOpen(false)}
        okText="Yes, Logout"
        okType="danger"
        cancelText="Cancel"
        width={400}
      >
        <p>Are you sure you want to logout?</p>
      </Modal>
    </>
  );
};

export default TopBar;
